import AppForms from "@toothfairy/shared-ui/AppForms";
import OnBoarding from "@toothfairy/shared-ui/AppOnBoardingScreen";
import AppSelection from "@toothfairy/shared-ui/AppSelection";
import { AppText } from "@toothfairy/shared-ui";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useEffect, useMemo, useState } from "react";
import { Platform, StyleSheet, View } from "react-native";
import ToothFairySubmitButton from "../../Components/ToothFairyComponents/ToothFairySubmitButton";
import Constants from "../../Constants";
import { User } from "../../models";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { useTranslation } from "react-i18next";
import { changeLanguage } from "../../../i18n.config";
import { isLargeScreen, minAppWidth } from "../MainViewContainer";
import usersAPI from "../../API/Users/usersAPI";
import usePolicyVersions from "../../Hooks/usePolicyVersions";

// Fast-signup context persisted by SignUp.js (sessionStorage) so the
// sourceCode/referralCode survive the redirect to "/". Read once, then
// consumed (cleared) so it is never reused for a later, unrelated signup.
const FAST_SIGNUP_STORAGE_KEY = "tf_fastSignupContext";
const FAST_SIGNUP_MAX_AGE_MS = 15 * 60 * 1000; // 15 minutes

const readFastSignupContext = () => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(FAST_SIGNUP_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - (parsed.ts || 0) > FAST_SIGNUP_MAX_AGE_MS) {
      window.sessionStorage.removeItem(FAST_SIGNUP_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch (e) {
    return null;
  }
};

const clearFastSignupContext = () => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    window.sessionStorage.removeItem(FAST_SIGNUP_STORAGE_KEY);
  } catch (e) {
    // Ignore storage errors.
  }
};

const OnboardingScreen = ({
  user,
  themeMode,
  setIsIncognito,
  updateUser,
  handleThemeSelect,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const [sliderIndex, setSliderIndex] = useState(1);
  const [sourceCode, setSourceCode] = useState(null);
  const { onboardingData } = Constants;
  const { t, i18n } = useTranslation();
  const inputFieldsWidth = Platform.OS === "web" ? 340 : 250;
  const dropdownWidth = Platform.OS === "web" ? 360 : 250;
  const AppToasty = AppToast.useToast();
  const [values, setValues] = useState({});
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const { currentPrivacyVersion } = usePolicyVersions();
  // Prefer the canonical version published by the website
  // (/policy-versions.json); fall back to the hard-coded constant so consent
  // recording never blocks on a fetch failure.
  const newsletterConsentTermsVersion =
    currentPrivacyVersion || Constants.newsletterConsent.termsVersion;

  useEffect(() => {
    const onLanguageChanged = (lng) => setCurrentLanguage(lng);
    i18n.on("languageChanged", onLanguageChanged);
    return () => i18n.off("languageChanged", onLanguageChanged);
  }, [i18n]);

  // Prefill the referral step from the fast-signup context. Prefer the
  // persisted sessionStorage context (survives the redirect to "/"); fall
  // back to the URL query string. Consume (clear) so it is never reused.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stored = readFastSignupContext();
    const urlSourceCode = urlParams.get("sourceCode") || stored?.sourceCode;
    const urlReferralCode =
      urlParams.get("referralCode") || stored?.referralCode;
    if (urlSourceCode || urlReferralCode) {
      setValues((prev) => ({
        ...prev,
        sourceCode: urlSourceCode
          ? urlSourceCode.toUpperCase()
          : prev.sourceCode,
        referralCode: urlReferralCode || prev.referralCode,
      }));
      if (urlSourceCode) {
        setSourceCode(urlSourceCode.toUpperCase());
      }
    }
    clearFastSignupContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleonBoardingSubmit = async (_values) => {
    try {
      if (
        _values.sourceCode == "PARTNER" ||
        (_values.sourceCode == "EVENT" &&
          _values.referralCode != null &&
          _values?.referralCode?.trim() != "")
      ) {
        const result = await usersAPI.findVoucher(_values.referralCode);
        if (result?.data?.valid == false) {
          return (
            AppToasty.show(result?.data?.reason),
            {
              type: "danger",
            }
          );
        }
      }

      const finalV = { ...values, ..._values };
      const consentGranted = finalV.subscribedToNewsletter === true;
      const userModel = await usersAPI.updateUser({
        id: user.id,
        isOnboarded: true,
        username: finalV.name,
        timezone: finalV.timezone,
        theme: finalV.theme,
        sourceCode: finalV.sourceCode,
        referralCode: finalV.referralCode,
        user_email: user?.user_email,
        user_phone_number: user?.user_phone_number,
        user_phone_number_verified: user?.user_phone_number_verified,
        subscribedToNewsletter: consentGranted,
        newsletterConsentTermsVersion,
        ...(consentGranted && {
          newsletterConsentedAt: Math.floor(Date.now() / 1000),
        }),
        newsletterConsentMethod: "onboarding",
        newsletterPromptShown: true,
        newsletterSubscriptionStatus: consentGranted ? "subscribed" : "pending",
        newsletterUpdatedAt: Math.floor(Date.now() / 1000),
      });
      setIsIncognito(false);
      updateUser({
        ...user,
        isOnboarded: true,
        username: finalV.name,
        timezone: finalV.timezone,
        theme: finalV.theme,
        sourceCode: finalV.sourceCode,
        referralCode: finalV.referralCode,
        subscribedToNewsletter: consentGranted,
        newsletterConsentTermsVersion,
        newsletterConsentMethod: "onboarding",
        newsletterPromptShown: true,
        newsletterSubscriptionStatus: consentGranted ? "subscribed" : "pending",
        ...(finalV.subscribedToNewsletter && {
          newsletterConsentedAt: Math.floor(Date.now() / 1000),
        }),
        newsletterUpdatedAt: Math.floor(Date.now() / 1000),
      });
      AppToasty.show(t("onboarding.success"), {
        type: "success",
      });
    } catch (error) {
      console.log("user creation error", error);
      AppToasty.show(t("onboarding.failed"), {
        type: "danger",
      });
    }
  };

  const handleFormSubmit = async (_values) => {
    if (_values.language && _values.language !== i18n.language) {
      await changeLanguage(_values.language);
    }
    setSliderIndex((prev) => prev + 1);
    setValues((prev) => ({ ...prev, ..._values }));
  };

  const getSourceOptions = () => {
    return [
      { id: null, name: t("onboarding.referral.sourceOptions.selectOne") },
      { id: "PARTNER", name: t("onboarding.referral.sourceOptions.partner") },
      { id: "EVENT", name: t("onboarding.referral.sourceOptions.event") },
      { id: "WEBSITE", name: t("onboarding.referral.sourceOptions.website") },
      {
        id: "SOCIAL_MEDIA",
        name: t("onboarding.referral.sourceOptions.socialMedia"),
      },
      { id: "GOOGLE", name: t("onboarding.referral.sourceOptions.google") },
      {
        id: "COLD_OUTREACH",
        name: t("onboarding.referral.sourceOptions.coldOutreach"),
      },
      {
        id: "ADVERTISING",
        name: t("onboarding.referral.sourceOptions.advertising"),
      },
      {
        id: "WORD_OF_MOUTH",
        name: t("onboarding.referral.sourceOptions.wordOfMouth"),
      },
      { id: "OTHER", name: t("onboarding.referral.sourceOptions.other") },
    ];
  };

  const steps = useMemo(
    () => [
      <OnBoarding.OnBoardingScreen
        isEnabledOnFilled
        logo={require("../../assets/logoTitle.png")}
        formInitialValues={
          values?.name
            ? {
                name: values.name,
              }
            : Constants.onboardingData.name.fieldInitialValue
        }
        formValidationSchema={onboardingData.name.fieldValidation}
        ContainerActionComponent={ToothFairySubmitButton}
        icon={onboardingData.name.icon}
        onFormSubmit={handleFormSubmit}
        title={t("onboarding.name.title")}
      >
        <AppForms.AppFormField
          style={{ textAlign: "center" }}
          isMandatory
          width={inputFieldsWidth}
          name={onboardingData.name.fieldName}
          placeholder={t("onboarding.name.placeholder")}
          autoFocus
        />
      </OnBoarding.OnBoardingScreen>,
      <OnBoarding.OnBoardingScreen
        isEnabledOnFilled
        logo={require("../../assets/logoTitle.png")}
        formInitialValues={
          values?.language
            ? {
                language: values.language,
              }
            : { language: i18n.language || "en" }
        }
        formValidationSchema={onboardingData.language.fieldValidation}
        ContainerActionComponent={ToothFairySubmitButton}
        icon={onboardingData.language.icon}
        onFormSubmit={handleFormSubmit}
        title={t("onboarding.language.title")}
      >
        <AppForms.AppFormDropDown
          formFieldContainerStyle={{
            flex: 1,
          }}
          isElevated={false}
          textAlign="center"
          dropDownStyle={{
            borderColor: currentTheme?.theme?.border_color,
            maxWidth: !isLargeScreen() && dropdownWidth,
            minWidth: dropdownWidth,
          }}
          isMandatory
          name={onboardingData.language.fieldName}
          values={onboardingData.language.fieldOptions}
          items={onboardingData.language.fieldOptions}
          propertyLabel="name"
          propertyValue="id"
          _onValueChange={(lng) => {
            if (lng && lng !== i18n.language) {
              changeLanguage(lng);
            }
          }}
        />
      </OnBoarding.OnBoardingScreen>,
      <OnBoarding.OnBoardingScreen
        isEnabledOnFilled
        logo={require("../../assets/logoTitle.png")}
        formInitialValues={
          values?.theme
            ? {
                theme: values.theme,
              }
            : onboardingData.theme.fieldInitialValue
        }
        formValidationSchema={onboardingData.theme.fieldValidation}
        ContainerActionComponent={ToothFairySubmitButton}
        icon={onboardingData.theme.icon}
        onFormSubmit={handleFormSubmit}
        title={t("onboarding.theme.title")}
        //   Added here to submit the info to DataStore at this step instead of the last ste
        onOnBoardingSubmit={handleonBoardingSubmit}
      >
        <AppForms.AppFormMultipleSelection
          width={Platform.OS === "web" ? 340 : 250}
          formOptionValue={"id"}
          height={Platform.OS === "web" ? "auto" : 66}
          optionsWidth={Platform.OS === "web" ? 150 : 110}
          selectedItem={[themeMode]}
          name={onboardingData.theme.fieldName}
          onSelectItem={(item) => handleThemeSelect(item.id)}
          options={onboardingData.theme.fieldOptions?.map((opt) => ({
            ...opt,
            title:
              opt.id === "light"
                ? t("onboarding.theme.light")
                : opt.id === "dark"
                ? t("onboarding.theme.dark")
                : opt.title,
            internalTitle:
              opt.id === "light"
                ? t("onboarding.theme.light")
                : opt.id === "dark"
                ? t("onboarding.theme.dark")
                : opt.internalTitle,
          }))}
          ContainerActionComponent={ToothFairySubmitButton}
          OptionItem={AppSelection.AppSimpleThemeSelectionItem}
          placeholder={t("onboarding.theme.placeholder")}
        />
      </OnBoarding.OnBoardingScreen>,
      <OnBoarding.OnBoardingScreen
        isEnabledOnFilled
        logo={require("../../assets/logoTitle.png")}
        formInitialValues={
          values?.sourceCode || values?.referralCode
            ? {
                sourceCode: values.sourceCode || "",
                referralCode: values.referralCode || "",
                subscribedToNewsletter: values?.subscribedToNewsletter ?? false,
              }
            : Constants.onboardingData.referral.fieldInitialValue
        }
        formValidationSchema={onboardingData.referral.fieldValidation}
        ContainerActionComponent={ToothFairySubmitButton}
        icon={onboardingData.referral.icon}
        onFormSubmit={handleFormSubmit}
        title={t("onboarding.referral.title")}
        isLast
        onOnBoardingSubmit={handleonBoardingSubmit}
      >
        <View style={styles.referralContent}>
          <AppForms.AppFormDropDown
            formFieldContainerStyle={{
              flex: 1,
            }}
            isElevated={false}
            dropDownStyle={{
              borderColor: currentTheme?.theme?.border_color,
              maxWidth: !isLargeScreen() && dropdownWidth,
              minWidth: dropdownWidth,
            }}
            isMandatory
            name="sourceCode"
            values={getSourceOptions()}
            items={getSourceOptions()}
            propertyLabel="name"
            propertyValue="id"
            placeholder={t("onboarding.referral.sourceCodeLabel")}
            _onValueChange={(e) => {
              setSourceCode(e);
            }}
          />
          {(sourceCode === "PARTNER" || sourceCode === "EVENT") && (
            <AppForms.AppFormField
              style={{ textAlign: "center" }}
              isMandatory={sourceCode !== "EVENT"}
              width={360}
              name="referralCode"
              placeholder={
                sourceCode === "EVENT"
                  ? t("onboarding.referral.eventCodeLabel")
                  : t("onboarding.referral.partnerCodeLabel")
              }
            />
          )}
          <View style={styles.consentContainer}>
            <AppForms.AppFormSwitch
              name="subscribedToNewsletter"
              showTextInputLabel
              label={t("onboarding.referral.consent.label")}
              description={t("onboarding.referral.consent.labelDesc")}
              descriptionIcon={false}
              descriptionColor={currentTheme?.theme?.mediumGrey}
              wrapperStyle={{
                paddingBottom: 8,
                width: "-webkit-fill-available",
                marginHorizontal: 7,
                marginTop: 0,
              }}
            />
            <View style={styles.consentTermsContainer}>
              <AppText
                style={{
                  fontSize: currentTheme?.theme?.extra_small_font_size,
                  marginBottom: 2,
                  textAlign: "left",
                }}
                color={currentTheme?.theme?.mediumGrey}
              >
                {"\u2022 " + t(Constants.newsletterConsent.terms.what)}
              </AppText>
              <AppText
                style={{
                  fontSize: currentTheme?.theme?.extra_small_font_size,
                  textAlign: "left",
                }}
                color={currentTheme?.theme?.mediumGrey}
              >
                {"\u2022 " + t(Constants.newsletterConsent.terms.howWithdraw)}
              </AppText>
            </View>
          </View>
        </View>
      </OnBoarding.OnBoardingScreen>,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ],
    [currentLanguage, values, sourceCode, themeMode],
  );

  return (
    <View
      style={{
        width: "100%",
      }}
    >
      <OnBoarding.AppOnBoardingScreen
        sliderItems={steps}
        sliderIndex={sliderIndex}
        setSliderIndex={setSliderIndex}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  referralContent: {
    width: "100%",
    alignSelf: "center",
    alignItems: "center",
  },
  consentContainer: {
    marginTop: 0,
    width: "100%",
    alignItems: "center",
  },
  consentTermsContainer: {
    marginTop: 0,
    marginLeft: 10,
    paddingHorizontal: 0,
    width: "100%",
    alignSelf: "stretch",
  },
});

export default OnboardingScreen;
